'use client';

import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import {
    AutosaveState
} from "@/app/lib/hooks/useAutosave";
import {
    PublishOptions,
    showPublishResultToast,
} from "@/app/lib/hooks/use-editor/publish-result";

type ChildEditorSnapshot = {
    hasChanges: boolean;
    autosaveState: AutosaveState | null;
};

type ChildEditorActions = {
    publish: (options?: PublishOptions) => Promise<boolean> | boolean;
    discardChanges: () => Promise<void> | void;
};

type SiteExhibitionsOverviewEditorContextType = {
    registerChildEditor: (documentId: string, snapshot: ChildEditorSnapshot, actions: ChildEditorActions) => void;
    unregisterChildEditor: (documentId: string) => void;
};

const SiteExhibitionsOverviewEditorContext = createContext<SiteExhibitionsOverviewEditorContextType | undefined>(undefined);

export function useSiteExhibitionsOverviewEditor() {
    const context = useContext(SiteExhibitionsOverviewEditorContext);
    if (!context) {
        throw new Error("useSiteExhibitionsOverviewEditor must be used within SiteExhibitionsOverviewEditorProvider");
    }
    return context;
}

export default function SiteExhibitionsOverviewEditorProvider({
    children,
    isDraftModeEnabled,
}: {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}) {
    const [childEditorSnapshots, setChildEditorSnapshots] = useState<Record<string, ChildEditorSnapshot>>({});
    const childEditorActionsRef = useRef<Map<string, ChildEditorActions>>(new Map());

    const registerChildEditor = useCallback((documentId: string, snapshot: ChildEditorSnapshot, actions: ChildEditorActions) => {
        childEditorActionsRef.current.set(documentId, actions);
        setChildEditorSnapshots((prev) => {
            const current = prev[documentId];
            if (
                current
                && current.hasChanges === snapshot.hasChanges
                && current.autosaveState === snapshot.autosaveState
            ) {
                return prev;
            }

            return {
                ...prev,
                [documentId]: snapshot,
            };
        });
    }, []);

    const unregisterChildEditor = useCallback((documentId: string) => {
        childEditorActionsRef.current.delete(documentId);
        setChildEditorSnapshots((prev) => {
            if (!(documentId in prev)) return prev;

            const next = { ...prev };
            delete next[documentId];
            return next;
        });
    }, []);

    const hasChanges = useMemo(
        () => Object.values(childEditorSnapshots).some((snapshot) => snapshot.hasChanges),
        [childEditorSnapshots]
    );

    const autosaveState = useMemo<AutosaveState>(() => {
        const states = Object.values(childEditorSnapshots).map((snapshot) => snapshot.autosaveState);
        if (states.some((state) => state === 'running')) return 'running';
        if (states.some((state) => state === 'error')) return 'error';
        if (states.some((state) => state === 'success')) return 'success';
        return null;
    }, [childEditorSnapshots]);

    const publish = useCallback(async () => {
        const changedChildActions = Array.from(childEditorActionsRef.current.entries())
            .filter(([documentId]) => childEditorSnapshots[documentId]?.hasChanges)
            .map(([, actions]) => actions);
        const results = await Promise.all(
            changedChildActions.map((actions) => actions.publish({ notify: false }))
        );
        const succeeded = results.every(Boolean);

        showPublishResultToast(succeeded);
        return succeeded;
    }, [childEditorSnapshots]);

    const discardChanges = useCallback(async () => {
        await Promise.all(
            Array.from(childEditorActionsRef.current.values()).map((actions) => actions.discardChanges())
        );
    }, []);

    return (
        <SiteExhibitionsOverviewEditorContext.Provider value={{ registerChildEditor, unregisterChildEditor }}>
            {children}
            <FloatingEditorMenu
                autosaveState={autosaveState || undefined}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={hasChanges}
                onPublish={publish}
                onDiscard={() => {
                    void discardChanges();
                }}
            />
        </SiteExhibitionsOverviewEditorContext.Provider>
    )
}
